import type { ApiAbsenceType, ApiShiftType } from '@/api/data';
/**
 * The empty-cell fast path: one list of everything that can fill the cell. Shift types
 * first, by how often the company uses them, then the absence types under a FRAVÆR
 * heading. The manager is choosing what goes in the cell, not choosing between two
 * features, so there is one search box and one list (owner, 2026-09-18).
 *
 * Picking a shift type creates the shift as a draft, as before. Picking an absence type
 * opens the absence drawer with that type chosen: a timed type has no clocks yet, and a
 * deducting one charges the balance the moment it is written, so neither is registered
 * from a popover without the manager seeing the dates and the amount.
 */
import { type Project, projectLabel } from '@/api/projects';
import { T } from '@/i18n';
import { kindOf } from '@/pages/fravaer/AbsenceRange';
import { typeTime } from '@/rules';
import { useEffect, useRef, useState } from 'react';

export interface QuickAt {
  userId: number | null;
  date: string;
  x: number;
  y: number;
  who: string;
}

/** Above this many rows, all of them, the list gets a search box. */
const SEARCHABLE = 8;

export function Quick({
  at,
  types,
  usage,
  absenceTypes,
  onPick,
  onMore,
  onAbsence,
  onClose,
  project: projectOpt,
}: {
  at: QuickAt;
  types: ApiShiftType[];
  usage: Map<number, number>;
  /** active absence types in the company's own order; empty when the row has no person or the role may not register one */
  absenceTypes?: ApiAbsenceType[];
  /** the type picked, and the project the shift is made with where the company uses projects (#115) */
  onPick: (type: ApiShiftType, projectId: number | null) => void;
  /** the company's projects where it uses them (#115): the active ones, its word, whether one is required */
  project?:
    | { projects: Project[]; required: boolean; word: { one: string; none: string; lower: string } }
    | undefined;
  onMore: () => void;
  onAbsence?: (type: ApiAbsenceType) => void;
  onClose: () => void;
}) {
  const [q, setQ] = useState('');
  const ref = useRef<HTMLElement>(null);
  // the project the shift is made with (#115): the last one chosen in this browser by default
  const projectsOn = !!projectOpt;
  const projectRequired = !!projectOpt?.required;
  const projectWord = projectOpt?.word ?? { one: '', none: '', lower: '' };
  const projects = projectOpt?.projects ?? [];
  const [projectId, setProjectId] = useState<number | null>(() => {
    try {
      return Number(localStorage.getItem('tf_last_plan_project')) || null;
    } catch {
      return null;
    }
  });
  const project = projects.some((p) => p.id === projectId) ? projectId : null;
  const pick = (t: ApiShiftType) => {
    if (project)
      try {
        localStorage.setItem('tf_last_plan_project', String(project));
      } catch {
        /* private mode: the choice is not remembered */
      }
    onPick(t, projectsOn ? project : null);
  };
  useEffect(() => {
    const down = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const key = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('mousedown', down);
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('mousedown', down);
      document.removeEventListener('keydown', key);
    };
  }, [onClose]);

  const absences = onAbsence ? (absenceTypes ?? []) : [];
  const hit = (name: string) => name.toLowerCase().includes(q.toLowerCase());
  const list = types
    .filter((t) => hit(t.name))
    .sort((a, b) => (usage.get(b.id) ?? 0) - (usage.get(a.id) ?? 0));
  // the absence rows keep the company's order whatever is typed: absence is rarer and
  // lumpier than a shift, so ordering it by use would read as random (owner, 2026-09-18)
  const abs = absences.filter((t) => hit(t.name));
  // the threshold counts every row the list can show, shifts and absences together,
  // because one list is what the person scrolls
  const many = types.length + absences.length > SEARCHABLE;
  const top = q ? [] : list.slice(0, 4);
  const rest = q ? list : list.slice(4);
  // "hele dagen" is only worth printing where the types differ; on a company whose types
  // are all full-day it is the same word on every row
  const mixed = new Set(absences.map((t) => kindOf(t.type))).size > 1;
  const left = Math.min(at.x, window.innerWidth - 240);
  const top_ = Math.min(at.y, window.innerHeight - 340);

  // with a project required, the list waits until one is chosen
  const blocked = projectsOn && projectRequired && !project;
  const row = (t: ApiShiftType) => (
    <button type="button" className="qt" key={t.id} disabled={blocked} onClick={() => pick(t)}>
      <s style={{ background: t.color_code || 'var(--control)' }} />
      <span className="qn">{t.name}</span>
      <i>
        {typeTime(t.start_time)}–{typeTime(t.end_time)}
      </i>
    </button>
  );
  const absRow = (t: ApiAbsenceType) => (
    <button type="button" className="qt" key={`a${t.id}`} onClick={() => onAbsence?.(t)}>
      <s style={{ background: t.color_code || 'var(--slate-l)' }} />
      <span className="qn">
        {t.name}
        <span className="tag2">{t.is_paid ? T('betalt') : T('ubetalt')}</span>
      </span>
      {mixed && <i>{kindOf(t.type) === 'date' ? T('hele dagen') : T('klokkeslett')}</i>}
    </button>
  );

  return (
    <section className="quick" ref={ref} style={{ left, top: top_ }} aria-label={T('Ny vakt')}>
      <div className="qh">
        <b>{at.who}</b>, {at.date}
      </div>
      {projectsOn && (
        <div className="qs" data-testid="quick-project">
          <select
            className="inp"
            aria-label={projectWord.one}
            value={project ?? ''}
            onChange={(e) => setProjectId(Number(e.target.value) || null)}
          >
            <option value="">
              {projectRequired ? `${T('Velg')} ${projectWord.lower} …` : projectWord.none}
            </option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {projectLabel(p)}
              </option>
            ))}
          </select>
        </div>
      )}
      {many && (
        <div className="qs">
          <input
            type="text"
            placeholder={T('Søk vakttype eller fravær…')}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
      )}
      <div className="qlist">
        {top.length > 0 && <h6>{T('Mest brukt')}</h6>}
        {top.map(row)}
        {rest.length > 0 && <h6>{q ? T('Vakt') : T('Alle vakttyper')}</h6>}
        {rest.map(row)}
        {/* the heading stays over the matches: typing "fe" finds a shift type and Ferie
            both, and booking a shift while meaning a holiday is the mistake to design against */}
        {abs.length > 0 && <h6>{T('Fravær')}</h6>}
        {abs.map(absRow)}
        {!list.length && !abs.length && <div className="qnone">{T('Ingen treff')}</div>}
      </div>
      <h6 style={{ borderTop: '1px solid var(--hair)' }}>
        <button type="button" className="btn ghost sm" onClick={onMore} style={{ padding: '2px 0' }}>
          {T('Flere valg › andre tider, notat')}
        </button>
      </h6>
    </section>
  );
}
